// @ts-nocheck
import { nav, routes, routes_ibo } from 'src/navigation/routes'
import { Breadcrumb, MenuProps } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useApp } from 'src/context/app.context'
import useToggle from 'src/hooks/useToggle'
import { IRoute } from 'src/models/navigation.model'
import useResponsive from 'src/hooks/useResponsive'
import { useAuth } from 'src/context/auth.context'
import { USER_ROLE_ENUM } from 'src/enums/enums'
import { useI18n } from 'src/context/i18n.context'

// TODO: Make Recursive
const HIDE_SIDEBAR_DESKTOP_MENUS = ['/stock-order/buy-stock']

const useMainLayoutController = () => {
  const { breadcrumbs } = useApp()
  const { getUserRole } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const { pathname } = location
  const { isDesktop } = useResponsive()

  const [isDrawerVisible, setIsDrawerVisible] = useToggle(false)
  const [isSidebarVisible, setIsSidebarVisible] = useToggle(true)

  const [hideSidebarDesktop, setHideSidebarDesktop] = useState(false)

  type MenuItem = Required<MenuProps>['items'][number]

  const _onSetNavItem = useCallback((
    label: React.ReactNode,
    key?: React.Key | null,
    icon?: React.ReactNode,
    children?: MenuItem[],
    type?: string,
  ): MenuItem => {
    return {
      key,
      icon,
      children,
      label,
      type,
    } as MenuItem
  }, [])

  const translateRouteLabel = useCallback((route: IRoute) => {
    return t(route.label, route.label)
  }, [t])

  const _getRoutes = useMemo(() => {
    return getUserRole === USER_ROLE_ENUM?.ibo ? routes_ibo : routes
  }, [getUserRole])

  const navItems: MenuProps['items'] = useMemo(() => {
    return nav?.flatMap((route) => {
      if (!route?.isNav) return []

      if (route?.isAllowed) {
        const isUserAllowed = route.isAllowed.includes(getUserRole)

        if (!isUserAllowed) return []
      }

      return route?.children?.length === 0
        ? _onSetNavItem(translateRouteLabel(route), route?.key, route?.icon)
        : _onSetNavItem(
            translateRouteLabel(route),
            route?.key,
            route?.icon,
            route?.children?.map((child) => ({
              key: child.key,
              label: translateRouteLabel(child),
            })),
            route?.type,
          )
    })
  }, [_onSetNavItem, getUserRole, translateRouteLabel])

  const OnSelectRoute = useCallback(({ routes, pathname }) => {
    for (const route of routes) {
      if (route.path === pathname) {
        return route
      }

      const matchedChild = (route.children || []).find((child: IRoute) =>
        pathname.startsWith(child.path),
      )

      if (matchedChild) {
        return {
          ...matchedChild,
          parentKey: [route.key],
          key: matchedChild.key,
        }
      }
    }

    return undefined
  }, [])

  const _onClickMenu: MenuProps['onClick'] = useCallback((e) => {
    const find = _getRoutes.find((item) => item.key === e.key)

    navigate(`${find?.path}`)
    isDrawerVisible && setIsDrawerVisible()
  }, [_getRoutes, isDrawerVisible, navigate, setIsDrawerVisible])

  const _onRenderBreadcrumbs = useCallback(() => {
    const items = breadcrumbs.map((item: any, index) => {
      let isActive: boolean = false

      if (pathname === item?.path) isActive = true

      return {
        title: (
          <Link to={item?.path} className={isActive ? 'link-active' : ''}>
            {item?.title}
          </Link>
        ),
      }
    })

    return <Breadcrumb items={items} />
  }, [breadcrumbs, pathname])

  const _handleToggleDrawer = () => setIsDrawerVisible()

  const _handleToggleSidebar = () => setIsSidebarVisible()

  const _selectedRouteKey = useMemo(() => {
    const res = OnSelectRoute({ routes: nav, pathname })

    return res
  }, [OnSelectRoute, pathname])

  const selectedMenuKeys = useMemo(() => {
    return _selectedRouteKey?.key ? [_selectedRouteKey.key] : []
  }, [_selectedRouteKey])

  useEffect(() => {
    if (HIDE_SIDEBAR_DESKTOP_MENUS.includes(pathname) && isDesktop) {
      setHideSidebarDesktop(true)
    } else {
      setHideSidebarDesktop(false)
    }
  }, [isDesktop, pathname])

  return {
    navItems,
    isDrawerVisible,
    isSidebarVisible,
    selectedRouteKey: _selectedRouteKey,
    selectedMenuKeys,
    hideSidebarDesktop,
    onClickMenu: _onClickMenu,
    onRenderBreadcrumbs: _onRenderBreadcrumbs,
    handleToggleDrawer: _handleToggleDrawer,
    handleToggleSidebar: _handleToggleSidebar,
  }
}

export default useMainLayoutController
